import { useEffect } from "react"
import { create } from "zustand"
import { loadVideoTemplateCatalog } from "~/common/videoTemplate/catalog"
import type { VideoTemplate } from "~/common/videoTemplate/types"

type TemplateEntry = {
  template?: VideoTemplate
  status: "idle" | "loading" | "ready" | "error"
  error: string
}
const IDLE_TEMPLATE: TemplateEntry = { status: "idle", error: "" }

type TemplateStore = {
  templates: VideoTemplate[]
  status: "idle" | "loading" | "ready" | "error"
  error: string
  load: (refresh?: boolean) => Promise<void>
  byId: Map<string, TemplateEntry>
  cacheTemplates: (templates: VideoTemplate[]) => void
  loadTemplate: (identity: string, refresh?: boolean) => Promise<void>
}

let pending: Promise<void> | null = null
const pendingTemplates = new Map<string, Promise<void>>()

export const useVideoTemplatesStore = create<TemplateStore>((set, get) => ({
  templates: [],
  status: "idle",
  error: "",
  byId: new Map(),
  cacheTemplates: (templates) =>
    set((state) => {
      const byId = new Map(state.byId)
      templates.forEach((template) =>
        byId.set(template.templateId, {
          template,
          status: "ready",
          error: "",
        }),
      )
      return { byId }
    }),
  loadTemplate: (identity, refresh = false) => {
    const active = pendingTemplates.get(identity)
    if (active) return active
    if (!refresh && get().byId.get(identity)?.status === "ready")
      return Promise.resolve()
    const update = (entry: TemplateEntry) =>
      set((state) => ({
        byId: new Map(state.byId).set(identity, entry),
      }))
    update({ status: "loading", error: "" })
    const operation = loadVideoTemplateCatalog({ template_id: identity })
      .then((templates) => {
        if (
          templates.length > 1 ||
          templates.some((template) => template.templateId !== identity)
        )
          throw new Error("模板查询返回数据无效，请重试")
        update({ template: templates[0], status: "ready", error: "" })
      })
      .catch((cause) =>
        update({
          status: "error",
          error:
            cause instanceof Error ? cause.message : "模板加载失败，请重试",
        }),
      )
      .finally(() => {
        pendingTemplates.delete(identity)
      })
    pendingTemplates.set(identity, operation)
    return operation
  },
  load: (refresh = false) => {
    if (pending) return pending
    if (!refresh && get().status === "ready") return Promise.resolve()
    set({ status: "loading", error: "" })
    pending = loadVideoTemplateCatalog()
      .then((templates) => {
        set({
          templates,
          status: "ready",
          byId: new Map(
            templates.map((template) => [
              template.templateId,
              { template, status: "ready", error: "" },
            ]),
          ),
        })
      })
      .catch((error) => {
        set({
          templates: [],
          status: "error",
          error:
            error instanceof Error ? error.message : "模板加载失败，请重试",
        })
      })
      .finally(() => {
        pending = null
      })
    return pending
  },
}))

// Consumers share an in-memory catalog and an in-flight request. Failed or empty
// responses never fall back to bundled templates. A page reload reads the server.
export const useVideoTemplates = () => {
  const templates = useVideoTemplatesStore((state) => state.templates)
  const status = useVideoTemplatesStore((state) => state.status)
  const error = useVideoTemplatesStore((state) => state.error)
  const load = useVideoTemplatesStore((state) => state.load)
  useEffect(() => {
    void load()
  }, [load])
  return { templates, status, error, reload: () => load(true) }
}

export const useVideoTemplate = (identity: string | undefined) => {
  const entry = useVideoTemplatesStore((state) =>
    identity ? (state.byId.get(identity) ?? IDLE_TEMPLATE) : IDLE_TEMPLATE,
  )
  const loadTemplate = useVideoTemplatesStore((state) => state.loadTemplate)
  useEffect(() => {
    if (identity) void loadTemplate(identity)
  }, [identity, loadTemplate])
  return {
    ...entry,
    reload: () => (identity ? loadTemplate(identity, true) : Promise.resolve()),
  }
}
